/**
 * 추천 콜라보 카드 서브타이틀(상태 줄)
 * @description collabStatus(+type, deadline)로 카드 하단 상태 라벨을 만든다. 진행형은
 *   "라벨 • N일 남음", 정산/완료형은 상태 단어만. 앱 `useSuggestedCollabSubtitle` 과 같은
 *   규칙 — 웹은 i18n 훅이 없어 순수 함수다.
 * @example toSuggestedCollabSubtitle(collab) // "Crew • 2d left" · "Submitted" · "Wrapping up"
 */
import { EXPLORE_COPY } from "../constants/copy";

/**
 * 상태 줄이 **실제로 읽는 것만** 받는다. 추천 카드 · 목록 줄 · 검색 결과가 서로 다른
 * 모양으로 오는데, 그중 하나의 전체 모양을 요구하면 나머지가 없는 필드를 지어내야 한다.
 */
export interface CollabStatusView {
  collabType: string;
  collabStatus: string;
  collabProjectDeadline?: string | null;
}

/** 콜라보 타입 라벨 — 앱 전역 영문 표기 */
const TYPE_LABEL: Record<string, string> = { CREW: "Crew", COMMUNITY: "Open" };
const MS_PER_DAY = 86_400_000;
const { daysLeft: daysLeftCopy, state } = EXPLORE_COPY.suggestedCollabs;

/** 마감까지 남은 일수(올림, 지났거나 없으면 0) */
const daysLeft = (deadline?: string | null, now = Date.now()): number => {
  if (!deadline) return 0;
  const diff = new Date(deadline).getTime() - now;
  return Math.max(0, Math.ceil(diff / MS_PER_DAY));
};

export function toSuggestedCollabSubtitle(
  collab: CollabStatusView,
  now = Date.now(),
): string {
  const days = daysLeft(collab.collabProjectDeadline, now);
  const countdown = days > 0 ? ` • ${daysLeftCopy(days)}` : "";
  const typeLabel = TYPE_LABEL[collab.collabType] ?? "Open";

  switch (collab.collabStatus) {
    case "SUBMITTED":
      return state.submitted;
    case "RECRUITMENT":
    case "READY_TO_GO":
      return `${state.forming}${countdown}`;
    case "IN_PROGRESS":
      return `${typeLabel}${countdown}`;
    case "ON_SETTLEMENT":
      return state.wrapping;
    case "DONE":
    case "SUCCESS":
    case "FAILURE":
      return state.completed;
    default:
      return `${typeLabel}${countdown}`;
  }
}
